// src/services/threadService.js
import db from '../db';

/**
 * Service for thread operations using IndexedDB
 */
const threadService = {
  /**
   * Get all threads for displaying in the sidebar
   * @returns {Promise<Array>} Array of thread objects without full message content
   */
  async getAllThreads() {
    try {
      // Get threads sorted by lastUpdated (newest first)
      const threads = await db.threads
        .orderBy('lastUpdated')
        .reverse()
        .toArray();
      
      return threads;
    } catch (error) {
      console.error("Error getting all threads:", error);
      throw error;
    }
  },
  
  /**
   * Get a complete thread with all its messages
   * @param {string} threadId The thread ID to retrieve
   * @returns {Promise<Object>} Complete thread with messages array
   */
  async getThread(threadId) {
    try {
      return await db.getCompleteThread(threadId);
    } catch (error) {
      console.error(`Error getting thread ${threadId}:`, error);
      throw error;
    }
  },
  
  /**
   * Create a new thread
   * @param {string} title Initial thread title
   * @param {string} model Claude model to use
   * @returns {Promise<string>} The new thread ID
   */
  async createThread(title = 'New Topic', model = 'claude-3-7-sonnet-20250219') {
    try {
      const id = crypto.randomUUID();
      const timestamp = Date.now();
      
      await db.threads.add({
        id,
        title,
        model,
        lastUpdated: timestamp,
        createdAt: timestamp
      });
      
      return id;
    } catch (error) {
      console.error("Error creating thread:", error);
      throw error;
    }
  },
  
  /**
   * Add a user message to a thread
   * @param {string} threadId Thread ID
   * @param {Object} message Message object with content, files, etc.
   * @returns {Promise<string>} The new message ID
   */
  async addUserMessage(threadId, message) {
    try {
      const messageId = `${threadId}-${Date.now()}`;
      const timestamp = Date.now();
      
      // Update thread lastUpdated timestamp
      await db.threads.update(threadId, { 
        lastUpdated: timestamp
      });
      
      // Add the message
      await db.messages.add({
        id: messageId,
        threadId,
        role: 'user',
        content: message.content || '',
        apiContent: message.apiContent || message.content || '',
        files: message.files || [],
        timestamp
      });
      
      return messageId;
    } catch (error) {
      console.error(`Error adding user message to thread ${threadId}:`, error);
      throw error;
    }
  },
  
  /**
   * Add an assistant (Claude) message to a thread
   * @param {string} threadId Thread ID
   * @param {string} content Message content
   * @param {Object} options Additional options
   * @returns {Promise<string>} The new message ID
   */
  async addAssistantMessage(threadId, content, options = {}) {
    try {
      const messageId = `${threadId}-${Date.now()}`;
      const timestamp = Date.now();
      
      // Update thread lastUpdated timestamp
      await db.threads.update(threadId, { 
        lastUpdated: timestamp
      });
      
      // Add the message
      await db.messages.add({
        id: messageId,
        threadId,
        role: 'assistant',
        content: content || '',
        timestamp,
        continuationInProgress: options.continuationInProgress || false,
        continuationComplete: options.continuationComplete || false
      });
      
      return messageId;
    } catch (error) {
      console.error(`Error adding assistant message to thread ${threadId}:`, error);
      throw error;
    }
  },
  
  /**
   * Update an existing message (used for continuations)
   * @param {string} messageId Message ID
   * @param {Object} updates Fields to update
   * @returns {Promise<void>}
   */
  async updateMessage(messageId, updates) {
    try {
      await db.messages.update(messageId, updates);
    } catch (error) {
      console.error(`Error updating message ${messageId}:`, error);
      throw error;
    }
  },
  
  /**
   * Delete a thread and all its messages
   * @param {string} threadId Thread ID to delete
   * @returns {Promise<void>}
   */
  async deleteThread(threadId) {
    try {
      await db.transaction('rw', [db.threads, db.messages], async () => {
        // Delete all messages for this thread
        await db.messages
          .where('threadId')
          .equals(threadId)
          .delete();
          
        // Delete the thread itself
        await db.threads.delete(threadId);
      });
    } catch (error) {
      console.error(`Error deleting thread ${threadId}:`, error);
      throw error;
    }
  },
  
  /**
   * Delete all threads and messages (for reset operations)
   * @returns {Promise<void>}
   */
  async deleteAllThreads() {
    try {
      await db.transaction('rw', [db.threads, db.messages], async () => {
        // Delete all messages
        await db.messages.clear();
        
        // Delete all threads
        await db.threads.clear();
      });
      
      console.log("All threads and messages deleted");
    } catch (error) {
      console.error("Error deleting all threads:", error);
      throw error;
    }
  },
  
  /**
   * Import a thread with all its messages from a backup
   * @param {Object} thread Thread object with messages array
   * @returns {Promise<string>} The imported thread ID
   */
  async importThread(thread) {
    try {
      const threadId = thread.id || crypto.randomUUID();
      const timestamp = Date.now();
      
      await db.transaction('rw', [db.threads, db.messages], async () => {
        // First add the thread
        await db.threads.put({
          id: threadId,
          title: thread.title || 'Imported Thread',
          model: thread.model || 'claude-3-7-sonnet-20250219',
          lastUpdated: thread.lastUpdated || timestamp,
          createdAt: thread.createdAt || timestamp
        });
        
        // Then add all messages if present
        if (thread.messages && Array.isArray(thread.messages)) {
          for (const message of thread.messages) {
            // Use the original message ID if available, otherwise generate a new one
            const messageId = message.id || `${threadId}-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
            
            await db.messages.put({
              id: messageId,
              threadId: threadId,
              role: message.role || 'user',
              content: message.content || '',
              apiContent: message.apiContent || message.content || '',
              files: message.files || [],
              timestamp: message.timestamp || Date.now(),
              continuationInProgress: message.continuationInProgress || false,
              continuationComplete: message.continuationComplete || false
            });
          }
        }
      });
      
      return threadId;
    } catch (error) {
      console.error("Error importing thread:", error);
      throw error;
    }
  },
  
  /**
   * Track thread opening to update last accessed time
   * @param {string} threadId Thread ID
   * @returns {Promise<void>}
   */
  async trackThreadOpening(threadId) {
    try {
      await db.threads.update(threadId, {
        lastAccessed: Date.now()
      });
    } catch (error) {
      console.error(`Error tracking thread opening ${threadId}:`, error);
      // Don't throw error as this is non-critical functionality
    }
  },
  
  /**
   * Rename a thread
   * @param {string} threadId Thread ID
   * @param {string} newTitle New thread title
   * @returns {Promise<void>}
   */
  async renameThread(threadId, newTitle) {
    try {
      await db.threads.update(threadId, { title: newTitle });
    } catch (error) {
      console.error(`Error renaming thread ${threadId}:`, error);
      throw error;
    }
  },
  
  /**
   * Search for messages containing a term
   * @param {string} searchTerm Term to search for
   * @returns {Promise<Array>} Array of matching threads with their matching messages
   */
  async searchMessages(searchTerm) {
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }
    
    try {
      const term = searchTerm.toLowerCase();
      const results = [];
      
      // Get all threads
      const threads = await db.threads.toArray();
      
      for (const thread of threads) {
        // Get messages for this thread
        const messages = await db.messages
          .where('threadId')
          .equals(thread.id)
          .toArray();
          
        // Filter messages that match the search term
        const matchingMessages = messages.filter(msg => 
          msg.content.toLowerCase().includes(term)
        );
        
        if (matchingMessages.length > 0) {
          results.push({
            thread,
            matchingMessages
          });
        }
      }
      
      return results;
    } catch (error) {
      console.error(`Error searching messages for "${searchTerm}":`, error);
      throw error;
    }
  },
  
  /**
   * Export a thread to JSON string
   * @param {string} threadId Thread ID
   * @returns {Promise<string>} JSON string of the thread
   */
  async exportThread(threadId) {
    try {
      const thread = await this.getThread(threadId);
      if (!thread) return null;
      
      return JSON.stringify(thread, null, 2);
    } catch (error) {
      console.error(`Error exporting thread ${threadId}:`, error);
      throw error;
    }
  },
  
  /**
   * Export all threads to JSON string
   * @returns {Promise<string>} JSON string of all threads
   */
  async exportAllThreads() {
    try {
      const threads = await db.threads.toArray();
      const fullThreads = [];
      
      for (const thread of threads) {
        const messages = await db.messages
          .where('threadId')
          .equals(thread.id)
          .toArray();
          
        fullThreads.push({
          ...thread,
          messages
        });
      }
      
      return JSON.stringify(fullThreads, null, 2);
    } catch (error) {
      console.error("Error exporting all threads:", error);
      throw error;
    }
  },
  
  /**
   * Get storage usage statistics
   * @returns {Promise<Object>} Storage stats object
   */
  async getStorageStats() {
    try {
      return await db.getStorageStats();
    } catch (error) {
      console.error("Error getting storage stats:", error);
      throw error;
    }
  }
};

export default threadService;